/**
 * EVENTSFLO-INBOX-PDF-INVOICE-REVIEW-UI-V1 (Phase 3)
 *
 * Self-contained review dialog that drives Phase 1 (read-only
 * extraction) and Phase 2 (confirmation-gated creation) of the
 * iCrew/uTrac invoice import workflow.
 *
 * Hard UI safety rules:
 *  - Extraction is read-only. Nothing is written to the DB until
 *    the user clicks "Create Project & Draft Shifts".
 *  - Create is disabled until: (a) extraction exists, (b) client
 *    is selected, (c) exact confirmation phrase is typed.
 *  - Negative or review_required lines disable Create.
 *  - Duplicate / total-mismatch blockers reveal an additional
 *    exact-phrase input.
 *  - No crew assignment / payment / email / provider control.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';

const CREATE_PHRASE = 'CREATE PROJECT FROM INVOICE PDF';
const DUP_OVERRIDE_PHRASE = 'OVERRIDE DUPLICATE INVOICE IMPORT';
const TOTAL_MISMATCH_PHRASE = 'IMPORT DESPITE TOTAL MISMATCH';

const fmt = (n) => (n == null ? '—' : Number(n).toFixed(2));

const FieldRow = ({ label, value, testid }) => (
  <div className="grid grid-cols-2 gap-2 py-1 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono break-all" data-testid={testid}>
      {value == null || value === '' ? '—' : String(value)}
    </span>
  </div>
);

export default function InvoiceImportReviewDialog({
  open,
  onOpenChange,
  attachment,
  messageId,
}) {
  const documentId = (attachment && attachment.id) || messageId
    || `inbox-${Date.now()}`;
  const filename = (attachment && attachment.original_filename) || '';

  const [rawText, setRawText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [dupOverride, setDupOverride] = useState('');
  const [tmOverride, setTmOverride] = useState('');

  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    if (!open) return;
    // reset on open
    setRawText('');
    setDraft(null);
    setSelectedClientId('');
    setConfirmPhrase('');
    setDupOverride('');
    setTmOverride('');
    setCreateResult(null);
    setCreateError(null);
    // pull tenant clients for the picker
    setClientsLoading(true);
    api.get('/clients?limit=200')
      .then((r) => setClients(Array.isArray(r.data) ? r.data : []))
      .catch(() => setClients([]))
      .finally(() => setClientsLoading(false));
  }, [open]);

  const runExtraction = async () => {
    if (!rawText.trim()) {
      toast.error('Paste the invoice text first.');
      return;
    }
    setExtracting(true);
    setDraft(null);
    try {
      const r = await api.post(
        `/admin/inbox/${encodeURIComponent(documentId)}/extract-invoice-project`,
        { raw_text: rawText, source_filename: filename },
      );
      setDraft(r.data);
      toast.success('Extraction complete (read-only).');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || 'Extraction failed',
      );
    } finally {
      setExtracting(false);
    }
  };

  const hasReviewRequired = useMemo(() => {
    if (!draft) return false;
    return (draft.line_items || []).some(
      (li) => li.review_required || (li.line_total != null && li.line_total < 0),
    );
  }, [draft]);

  const totalMismatch = draft && draft.total_matches === false;

  const canCreate = (
    !!draft
    && draft.extraction_status
    && ['ok', 'partial_review_required'].includes(draft.extraction_status)
    && !hasReviewRequired
    && !!selectedClientId
    && confirmPhrase === CREATE_PHRASE
    && (!createResult)
  );

  const runCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      const body = {
        extraction_draft: draft,
        selected_client_id: selectedClientId,
        confirmation_phrase: confirmPhrase,
        source_filename: filename,
      };
      if (dupOverride) body.duplicate_override_phrase = dupOverride;
      if (tmOverride) body.total_mismatch_override_phrase = tmOverride;
      const r = await api.post(
        `/admin/inbox/${encodeURIComponent(documentId)}/create-project-from-extraction`,
        body,
      );
      if (r.data && r.data.can_create === false) {
        setCreateError(r.data);
        toast.error('Creation blocked by backend — see blockers.');
      } else {
        setCreateResult(r.data);
        toast.success('Project & draft shifts created.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setCreateError(
        typeof detail === 'object'
          ? detail
          : { error: 'request_failed', message: detail || err?.message }
      );
      toast.error('Create call failed — see error panel.');
    } finally {
      setCreating(false);
    }
  };

  // -- blocker codes coming back from Phase 2 --
  const blockers = (createError && createError.blockers) || [];
  const blockerCodes = blockers.map((b) => b.code);
  const isDupBlocked = blockerCodes.includes('duplicate_invoice_import_blocked');
  const isTmBlocked = blockerCodes.includes('total_mismatch_requires_confirmation');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="invoice-import-review-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="invoice-import-review-title">
            Invoice import review
          </DialogTitle>
          <DialogDescription>
            Extraction is read-only. Project creation only happens after confirmation.
          </DialogDescription>
        </DialogHeader>

        {/* ---------- 1. Extraction step --------------------- */}
        <section className="space-y-2 border border-zinc-800 rounded-md p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Source:</span>{' '}
              <span data-testid="invoice-import-source-filename">{filename || documentId}</span>
            </div>
            <Badge variant="outline" data-testid="invoice-import-readonly-note">
              Read-only extraction
            </Badge>
          </div>
          <Label className="text-xs">Paste invoice text (PDF copy/paste)</Label>
          <Textarea
            rows={6}
            placeholder="Paste the iCrew/uTrac invoice text here…"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            data-testid="invoice-import-raw-text"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={runExtraction}
              disabled={extracting || !rawText.trim()}
              data-testid="invoice-import-extract-btn"
            >
              {extracting ? 'Extracting…' : 'Extract Project from Invoice PDF'}
            </Button>
          </div>
        </section>

        {/* ---------- 2. Review panel ------------------------ */}
        {draft && (
          <section
            className="space-y-3 border border-zinc-800 rounded-md p-3"
            data-testid="invoice-import-review-panel"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Extraction draft</h3>
              <div className="flex items-center gap-2">
                <Badge variant="outline" data-testid="invoice-import-extraction-status">
                  {draft.extraction_status}
                </Badge>
                <Badge variant="outline" data-testid="invoice-import-confidence">
                  confidence: {draft.confidence_summary?.overall || 'n/a'}
                </Badge>
                <Badge variant="outline">parser: {draft.parser_name}</Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-0">
              <FieldRow label="Client" value={draft.detected_client_name} testid="invoice-import-field-client" />
              <FieldRow label="Invoice #" value={draft.detected_invoice_number} testid="invoice-import-field-invoice-number" />
              <FieldRow label="Invoice date" value={draft.detected_invoice_date} testid="invoice-import-field-invoice-date" />
              <FieldRow label="Ref" value={draft.detected_ref} testid="invoice-import-field-ref" />
              <FieldRow label="PO #" value={draft.detected_po_number} testid="invoice-import-field-po-number" />
              <FieldRow label="Job / project" value={draft.detected_project_name} testid="invoice-import-field-project" />
              <FieldRow label="Venue" value={draft.detected_location_name} testid="invoice-import-field-venue-name" />
              <FieldRow label="Venue address" value={draft.detected_location_address} testid="invoice-import-field-venue-address" />
              <FieldRow label="Currency" value={draft.detected_currency} testid="invoice-import-field-currency" />
              <FieldRow label="Detected total" value={fmt(draft.detected_total_amount)} testid="invoice-import-field-detected-total" />
              <FieldRow label="Sum of line totals" value={fmt(draft.calculated_line_total)} testid="invoice-import-field-calculated-total" />
              <FieldRow label="Total matches?" value={String(draft.total_matches)} testid="invoice-import-field-total-matches" />
            </div>

            {/* Shifts */}
            <div>
              <div className="text-xs font-medium mb-1">
                Shifts ({(draft.shifts || []).length})
              </div>
              <table className="w-full text-xs" data-testid="invoice-import-shifts-table">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th>Date</th><th>Start</th><th>Duration (h)</th>
                    <th>Required crew</th><th>Venue</th>
                  </tr>
                </thead>
                <tbody>
                  {(draft.shifts || []).map((s, i) => (
                    <tr key={i} data-testid={`invoice-import-shift-row-${i}`}>
                      <td>{s.shift_date}</td>
                      <td>{s.start_time}</td>
                      <td>{fmt(s.duration_hours)}</td>
                      <td>{s.required_crew_count}</td>
                      <td>{s.location_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Line items */}
            <div>
              <div className="text-xs font-medium mb-1">
                Line items ({(draft.line_items || []).length})
              </div>
              <table className="w-full text-xs" data-testid="invoice-import-line-items-table">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th>Date/Time</th><th>Task</th><th>Type</th>
                    <th>Qty</th><th>Hours</th><th>Unit €</th>
                    <th>Travel</th><th>Total €</th><th>Review?</th>
                  </tr>
                </thead>
                <tbody>
                  {(draft.line_items || []).map((li, i) => (
                    <tr
                      key={i}
                      className={li.review_required ? 'text-rose-300' : ''}
                      data-testid={`invoice-import-line-row-${i}`}
                    >
                      <td>{li.shift_date} {li.start_time}</td>
                      <td>{li.task_name}</td>
                      <td>{li.task_type}</td>
                      <td>{li.quantity}</td>
                      <td>{fmt(li.hours)}</td>
                      <td>{fmt(li.unit_rate)}</td>
                      <td>{fmt(li.travel)}</td>
                      <td>{fmt(li.line_total)}</td>
                      <td>{li.review_required ? 'yes' : 'no'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Duplicate detection scaffold */}
            <div data-testid="invoice-import-duplicate-detection">
              <div className="text-xs font-medium mb-1">Duplicate detection</div>
              <ul className="text-xs space-y-0.5">
                {(draft.duplicate_detection || []).map((d, i) => (
                  <li key={i} className="flex gap-2">
                    <Badge variant="outline" className="text-[10px]">
                      {d.status}
                    </Badge>
                    <span className="text-muted-foreground">{d.check}:</span>
                    <span className="font-mono break-all">{String(d.value)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Warnings */}
            {Array.isArray(draft.warnings) && draft.warnings.length > 0 && (
              <div
                className="rounded-md border border-amber-700/40 bg-amber-900/20 p-2"
                data-testid="invoice-import-warnings"
              >
                <div className="text-xs font-medium text-amber-300 mb-1">Warnings</div>
                <ul className="text-xs list-disc list-inside text-amber-200">
                  {draft.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {hasReviewRequired && (
              <div
                className="rounded-md border border-rose-700/40 bg-rose-900/20 p-2 text-xs text-rose-200"
                data-testid="invoice-import-review-required-blocker"
              >
                This extraction contains review-required or negative lines.
                Resolve manually before creating project.
              </div>
            )}

            {totalMismatch && (
              <div
                className="rounded-md border border-amber-700/40 bg-amber-900/20 p-2 text-xs text-amber-200"
                data-testid="invoice-import-total-mismatch-warning"
              >
                Sum of line totals ({fmt(draft.calculated_line_total)}) does not match
                detected invoice total ({fmt(draft.detected_total_amount)}). Type the
                override phrase below to import anyway.
              </div>
            )}
          </section>
        )}

        {/* ---------- 3. Client + confirmation -------------- */}
        {draft && !createResult && (
          <section className="space-y-2 border border-zinc-800 rounded-md p-3">
            <Label className="text-xs">Client (required)</Label>
            <Select
              value={selectedClientId}
              onValueChange={setSelectedClientId}
            >
              <SelectTrigger data-testid="invoice-import-client-select">
                <SelectValue placeholder={clientsLoading ? 'Loading clients…' : 'Select an existing client…'} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!selectedClientId && (
              <div
                className="text-xs text-amber-300"
                data-testid="invoice-import-client-required-note"
              >
                Select an existing client before creating the project.
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 pt-2">
              <div>
                <Label className="text-xs">
                  Confirmation phrase — type exactly:{' '}
                  <code className="text-[10px]">{CREATE_PHRASE}</code>
                </Label>
                <Input
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder={CREATE_PHRASE}
                  data-testid="invoice-import-confirm-phrase"
                />
              </div>

              {(isDupBlocked || (draft && draft.detected_invoice_number)) && (
                <div>
                  <Label className="text-xs">
                    Duplicate-import override (only if backend blocks){' '}
                    <code className="text-[10px]">{DUP_OVERRIDE_PHRASE}</code>
                  </Label>
                  <Input
                    value={dupOverride}
                    onChange={(e) => setDupOverride(e.target.value)}
                    placeholder={DUP_OVERRIDE_PHRASE}
                    data-testid="invoice-import-dup-override-phrase"
                  />
                </div>
              )}

              {(isTmBlocked || totalMismatch) && (
                <div>
                  <Label className="text-xs">
                    Total-mismatch override{' '}
                    <code className="text-[10px]">{TOTAL_MISMATCH_PHRASE}</code>
                  </Label>
                  <Input
                    value={tmOverride}
                    onChange={(e) => setTmOverride(e.target.value)}
                    placeholder={TOTAL_MISMATCH_PHRASE}
                    data-testid="invoice-import-tm-override-phrase"
                  />
                </div>
              )}
            </div>

            <div className="pt-2 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="invoice-import-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={runCreate}
                disabled={!canCreate || creating}
                data-testid="invoice-import-create-btn"
              >
                {creating ? 'Creating…' : 'Create Project & Draft Shifts'}
              </Button>
            </div>
          </section>
        )}

        {/* ---------- 4. Error / blockers -------------------- */}
        {createError && (
          <section
            className="space-y-2 border border-rose-700/40 bg-rose-900/10 rounded-md p-3"
            data-testid="invoice-import-error-panel"
          >
            <div className="text-sm font-medium text-rose-300">
              Creation blocked
            </div>
            {createError.error && (
              <div className="text-xs">
                <span className="text-muted-foreground">code: </span>
                <code>{createError.error}</code>
              </div>
            )}
            {createError.message && (
              <div className="text-xs text-rose-200">{createError.message}</div>
            )}
            {Array.isArray(createError.blockers) && createError.blockers.length > 0 && (
              <ul className="text-xs space-y-1">
                {createError.blockers.map((b, i) => (
                  <li key={i} data-testid={`invoice-import-blocker-${b.code}`}>
                    <Badge variant="outline" className="mr-2">{b.code}</Badge>
                    <span className="text-rose-200">{b.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* ---------- 5. Success state ---------------------- */}
        {createResult && createResult.can_create && (
          <section
            className="space-y-2 border border-emerald-700/40 bg-emerald-900/10 rounded-md p-3"
            data-testid="invoice-import-success-panel"
          >
            <div className="text-sm font-medium text-emerald-300">
              Project & draft shifts created
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0">
              <FieldRow label="Project id" value={createResult.project_id} testid="invoice-import-success-project-id" />
              <FieldRow label="Venue id" value={createResult.venue_id} testid="invoice-import-success-venue-id" />
              <FieldRow label="Client id" value={createResult.client_id} testid="invoice-import-success-client-id" />
              <FieldRow label="Shifts created" value={createResult.created_shift_count} testid="invoice-import-success-shift-count" />
              <FieldRow label="Line items created" value={createResult.created_line_item_count} testid="invoice-import-success-line-count" />
              <FieldRow label="Import run id" value={createResult.import_run_id} testid="invoice-import-success-import-run-id" />
              <FieldRow label="No crew assignments" value={String(!!createResult.no_crew_assignments_created)} testid="invoice-import-success-no-crew" />
            </div>
            {Array.isArray(createResult.warnings) && createResult.warnings.length > 0 && (
              <ul className="text-xs list-disc list-inside text-amber-200">
                {createResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <div className="text-xs text-muted-foreground">
              Open the project from the Projects list to continue review or planning.
            </div>
            <div className="pt-1">
              <a
                href="/admin/backups#inbox-invoice-import-audit-panel"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-300 underline"
                data-testid="invoice-import-success-view-audit-link"
              >
                View import audit log →
              </a>
            </div>
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
